import { history } from '@umijs/max';
import {
  logoutRequest,
  queryCurrentMenus,
  queryCurrentUser,
  type AuthCurrentUser,
  type AuthMenu,
} from '@/services/trade/auth';
import { getFirstAccessiblePath } from '@/utils/route';

export const AUTH_TOKEN_KEY = 'trade_auto_token';
export const LOGIN_PATH = '/user/login';
export const DEFAULT_HOME_PATH = '/home';
export const ACCOUNT_SECURITY_PATH = '/account/security';
export const FORBIDDEN_PATH = '/403';

export type AppInitialState = {
  currentUser?: AuthCurrentUser;
  currentMenus?: AuthMenu[];
  passwordChangeToken?: string;
  settings?: Record<string, unknown>;
};

export const ACCESS_PERMISSION_MAP = {
  canViewHome: 'home:view',
  canViewDashboard: 'dashboard:view',
  canViewEntry7501: 'entry7501:view',
  canViewDeliveryOrder: 'delivery-order:view',
  canViewUsers: 'sys:user:view',
  canViewRoles: 'sys:role:view',
  canViewMenus: 'sys:menu:view',
  canViewRpaClients: 'sys:rpa-client:view',
  canViewRpaClientConfigs: 'sys:rpa-client-config:view',
  canViewRpaInstallPackages: 'sys:rpa-install-package:view',
  canViewRpaLogs: 'sys:rpa-log:view',
  canViewRpaRuntimeEvents: 'sys:rpa-runtime-event:view',
  canViewAceScheduleDPorts: 'sys:ace-schedule-d:view',
  canViewAceScheduleKPorts: 'sys:ace-schedule-k:view',
  canViewOceanCarrierScacDict: 'dict:ocean-carrier-scac:view',
  canViewHtsCodes: 'sys:hts-code:view',
  canViewMail: 'mail:view',
  canViewMailClassification: 'mail:classification:view',
  canViewIsf: 'isf:view',
  canViewNotify: 'notify:view',
  canViewNotifySendRecords: 'sys:notify-send-record:view',
  canViewAiPrompt: 'ai:prompt:view',
  canManageAiPrompt: 'ai:prompt:manage',
  canViewLlmStats: 'ai:llm:view',
  canViewExperimentFileImage: 'experiment:file-image:view',
  canViewExperimentImageBase64: 'experiment:image-base64:view',
} as const;

export function getAuthToken() {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(token: string) {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearAuthToken() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

export async function clearLocalSession(
  setInitialState?: (updater: (state: AppInitialState) => AppInitialState) => Promise<void> | void,
  redirectPath?: string,
) {
  clearAuthToken();
  if (setInitialState) {
    await setInitialState((state) => ({
      ...(state || {}),
      currentUser: undefined,
      currentMenus: [],
      passwordChangeToken: undefined,
    }));
  }
  redirectToLogin(redirectPath);
}

export async function logoutCurrentSession(
  setInitialState?: (updater: (state: AppInitialState) => AppInitialState) => Promise<void> | void,
  redirectPath?: string,
) {
  if (getAuthToken()) {
    await logoutRequest().catch(() => undefined);
  }
  await clearLocalSession(setInitialState, redirectPath);
}

export function isLoginPath(pathname?: string) {
  return !!pathname && pathname.startsWith(LOGIN_PATH);
}

export function isAccountPath(pathname?: string) {
  return !!pathname && pathname.startsWith('/account');
}

export function isForbiddenPath(pathname?: string) {
  return pathname === FORBIDDEN_PATH;
}

export function isPasswordChangeRequired(currentUser?: AuthCurrentUser) {
  return currentUser?.mustChangePassword === 1;
}

export function sanitizeRedirectPath(path?: string | null) {
  if (!path || !path.startsWith('/')) {
    return undefined;
  }
  if (path.startsWith('//') || isLoginPath(path)) {
    return undefined;
  }
  return path;
}

export function getLoginRedirectPath(search?: string) {
  const queryString = search ?? history.location.search ?? '';
  const redirect = new URLSearchParams(queryString).get('redirect');
  return sanitizeRedirectPath(redirect);
}

export function redirectToLogin(redirectPath?: string) {
  if (isLoginPath(history.location.pathname)) {
    return;
  }
  const redirect = sanitizeRedirectPath(redirectPath);
  const nextLocation = {
    pathname: LOGIN_PATH,
    search: redirect ? `?redirect=${encodeURIComponent(redirect)}` : '',
  };
  if (
    history.location.pathname === nextLocation.pathname &&
    (history.location.search || '') === nextLocation.search
  ) {
    return;
  }
  history.replace(nextLocation);
}

export function redirectToHome(initialState?: AppInitialState) {
  if (isPasswordChangeRequired(initialState?.currentUser)) {
    if (history.location.pathname !== ACCOUNT_SECURITY_PATH) {
      history.replace(ACCOUNT_SECURITY_PATH);
    }
    return;
  }
  const fallbackPath = getFirstAccessiblePath(
    initialState?.currentUser,
    initialState?.currentMenus || [],
  );
  const nextPath = fallbackPath || DEFAULT_HOME_PATH;
  if (history.location.pathname === nextPath) {
    return;
  }
  history.replace(nextPath);
}

export function redirectToForbidden() {
  if (history.location.pathname === FORBIDDEN_PATH) {
    return;
  }
  history.replace(FORBIDDEN_PATH);
}

export function resolvePostLoginPath(initialState?: AppInitialState, search?: string) {
  if (isPasswordChangeRequired(initialState?.currentUser)) {
    return ACCOUNT_SECURITY_PATH;
  }
  const redirectPath = getLoginRedirectPath(search);
  if (redirectPath && redirectPath !== '/data-dashboard') {
    return redirectPath;
  }
  return getFirstAccessiblePath(initialState?.currentUser, initialState?.currentMenus || []);
}

export async function loadInitialAppState(): Promise<AppInitialState> {
  const token = getAuthToken();
  if (!token) {
    return { currentMenus: [], settings: {} };
  }

  try {
    const currentUser = await queryCurrentUser(token);
    if (isPasswordChangeRequired(currentUser)) {
      clearAuthToken();
      return {
        currentUser,
        currentMenus: [],
        passwordChangeToken: token,
        settings: {},
      };
    }
    const currentMenus = await queryCurrentMenus(token);
    return { currentUser, currentMenus, settings: {} };
  } catch {
    clearAuthToken();
    return { currentMenus: [], settings: {} };
  }
}

export function buildAccessState(initialState?: AppInitialState) {
  const permissions = new Set(initialState?.currentUser?.permissions || []);
  const accessState: Record<string, boolean> = {
    canReadTrade: !!initialState?.currentUser,
  };

  Object.entries(ACCESS_PERMISSION_MAP).forEach(([key, permission]) => {
    accessState[key] = permissions.has(permission);
  });

  return accessState as {
    canReadTrade: boolean;
  } & Record<keyof typeof ACCESS_PERMISSION_MAP, boolean>;
}
